import {
  Box,
  Code,
  Flex,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { capitalCase } from 'change-case';
import { useRouter } from 'next/router';
import { PropsWithChildren, useState } from 'react';

import Notification from '@/components/Notification';
import PackageIcon from '@/components/PackageIcon';
import PageTemplate from '@/components/PageTemplate';
import PrismHighlighter from '@/components/PrismHighlighter';
import RouteLink from '@/components/RouteLink';
import TabGroup from '@/components/TabGroup';
import { useAccounts } from '@/contexts/accounts';
import { useCurrentUser } from '@/contexts/currentUser';
import { RegistryPackage, RegistryPackageType, useUpdateUserSettingsMutation } from '@/graphql/upbound-graphql';
import { CONFIG_REGISTRY_HOST } from '@/utils/constants/config';
import { PackageTabs } from '@/utils/constants/tabs';
import { accountsLoginUrl, UPBOUND_DOCS_URL } from '@/utils/constants/urls';
import { tryCatch } from '@/utils/helpers/routing';
import usePageScrollPadding from '@/utils/hooks/usePageScrollPadding';
import usePageScrollTo from '@/utils/hooks/usePageScrollTo';

import FamilyProviderHeader from './__components__/FamilyProviderHeader';
import PackageHeader from './__components__/PackageHeader';
import useTabs from './_useTabs';

function getBaseCodeBlock(data: RegistryPackage) {
  const packageUrl = `${CONFIG_REGISTRY_HOST}/${data.account}/${data.name}:${data.version}`;

  return `apiVersion: pkg.crossplane.io/v1
kind: ${capitalCase(data.packageType)}
metadata:
  name: ${data.name}
spec:
  package: ${packageUrl}`;
}

function getPrivateCodeBlock() {
  return `
  packagePullSecrets:
    - name: package-pull-secret`;
}

function getCodeBlock(data: RegistryPackage) {
  const base = getBaseCodeBlock(data);

  if (data.public === false) {
    return base + getPrivateCodeBlock();
  }

  return base;
}

const PackageDetailsLayout: React.FC<
  PropsWithChildren<PageProps.PackageDetails & { requestedTabType?: PackageTabs }>
> = ({ data, requestedTabType }) => {
  const [latestVersion = 'latest'] = data.versions || [];
  const [isInstallModalVisible, setIsInstallModalVisible] = useState(false);
  const { allTabs, activeTab, setActiveTab } = useTabs({ data, requestedTabType });
  const [mobileRef] = usePageScrollPadding();
  const [scrollRef] = usePageScrollTo(!requestedTabType);
  const [isStarred, setIsStarred] = useState<boolean>(!!data.userSettings?.starred);
  const { currentUser } = useCurrentUser();
  const router = useRouter();
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const onStarClick = () => {
    if (!currentUser) {
      router.push(accountsLoginUrl(router.asPath));
    } else {
      updateUserSettings({
        variables: {
          payload: {
            account: data.account,
            name: data.name,
            starred: !isStarred,
          },
        },
      })
        .then(() => {
          setIsStarred(!isStarred);
        })
        .catch(error => tryCatch(error));
    }
  };

  // TODO: When releasing Family Providers to GA, remove check for Upbound account
  const { accounts } = useAccounts();
  const isUpboundMember = !!accounts.find(a => a.id === 'upbound');
  const { repositoryId, familyRepositoryId } = data;
  const isFamilyProvider = familyRepositoryId && repositoryId === familyRepositoryId;

  return (
    <PageTemplate title={`${data.account}/${data.name}@${data.version}`} mobileRef={mobileRef}>
      <Box
        width="100%"
        maxWidth="1140px"
        p={{ base: '30px', md: '50px 70px 0 70px', xl: '50px 15px 0 15px' }}
        m="0 auto"
      >
        {data.versions && data.version !== 'latest' && data.version !== data.versions[0] && (
          <Notification mb="29px">
            <Text color="grey.0" as="span">
              You are viewing an outdated version of this {data.packageType.toLowerCase()}.
            </Text>
            <RouteLink
              to="packageDetails"
              params={{
                packageType: data.packageType,
                packageOwner: data.account,
                packageName: data.name,
                packageVersion: latestVersion,
              }}
              ml="12px"
              color="purple.0"
              textDecoration="underline"
              _hover={{ textDecoration: 'none' }}
            >
              Go to Latest
            </RouteLink>
          </Notification>
        )}
        {isUpboundMember && isFamilyProvider ? (
          <FamilyProviderHeader
            data={data}
            setIsInstallModalVisible={setIsInstallModalVisible}
            onStarClick={onStarClick}
            isStarred={isStarred}
          ></FamilyProviderHeader>
        ) : (
          <PackageHeader
            data={data}
            setIsInstallModalVisible={setIsInstallModalVisible}
            onStarClick={onStarClick}
            isStarred={isStarred}
          ></PackageHeader>
        )}
      </Box>
      <Box width="100%" height="1px" background="white.4" mt={{ base: '35px', md: '70px' }} />
      <Box ref={scrollRef} />
      <Box
        width="100%"
        maxWidth="1140px"
        p={{ base: '50px 30px 30px 30px', md: '50px 70px 0 70px', xl: '50px 15px 0 15px' }}
        m="0 auto"
      >
        <Box>
          {data.packageType !== RegistryPackageType.Provider && data.dependsOn.length > 1 && (
            <Box mb="50px">
              <Text
                as="span"
                fontWeight={900}
                color="black.0"
                fontSize="20px"
                lineHeight="25px"
                mb="20px"
                display="inline-block"
              >
                Dependencies
              </Text>
              <Box mb={{ base: '30px', md: '19px' }}>
                <Text display="inline" color="grey.0" lineHeight="25px">
                  Dependencies are packages that this {data.packageType.toLowerCase()} uses.
                </Text>
              </Box>
              <Box>
                {data.dependsOn.map((dependency, index) => {
                  const [packageName] = dependency.displayName.split('/').reverse();
                  return (
                    <Flex
                      width="100%"
                      borderBottom="1px solid"
                      borderBottomColor="white.4"
                      alignItems={{ base: 'flex-start', md: 'center' }}
                      flexWrap="wrap"
                      flexDirection={{ base: 'column', md: 'row' }}
                      p={{ base: '30px 0', md: '30px 15px 28px 15px' }}
                      borderTopWidth="0px"
                      borderTopStyle="solid"
                      borderTopColor="white.4"
                      key={`${dependency.id}-${dependency.packageType}-${dependency.version}-${index}`}
                      _first={{ borderTopWidth: '1px' }}
                    >
                      <Text display={{ base: 'block', md: 'inline' }} fontWeight={900} color="black.0">
                        {packageName}
                      </Text>
                      <Box mt={{ base: '10px', md: '18px' }} width="100%">
                        <Text
                          display={{ base: 'inline', md: 'none' }}
                          as="span"
                          color="grey.0"
                          fontSize="14px"
                          lineHeight="20px"
                        >
                          {capitalCase(dependency.packageType.toLowerCase())}
                        </Text>
                        <Text
                          display={{ base: 'none', md: 'inline' }}
                          as="span"
                          color="grey.1"
                          fontSize="14px"
                          lineHeight="20px"
                        >
                          {dependency.displayName}
                        </Text>
                      </Box>
                    </Flex>
                  );
                })}
              </Box>
            </Box>
          )}
          <TabGroup allTabs={allTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
        </Box>
      </Box>
      <Modal size="3xl" isOpen={isInstallModalVisible} onClose={() => setIsInstallModalVisible(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <PackageIcon data={data} width="48px" height="48px" mb="10px" />
            <Heading as="h4" size="sm" mb="20px">
              Install {data.displayName}
            </Heading>
            <Text mb="30px">
              The following manifest can be used to install this package in your control plane. After you have connected
              to your control plane run <Code padding="0px 5px 0px 5px">kubectl apply -f</Code> after copying the
              following into a local file.
            </Text>
            <PrismHighlighter
              language="yaml"
              plugins={['line-numbers', 'copy-to-clipboard']}
              copyIconProps={{ className: 'copy-manifest-button' }}
              mb="55px"
            >
              {getCodeBlock(data)}
            </PrismHighlighter>
            <Text textAlign="center" size="sm" color="grey.1">
              For more detailed instructions on how to install packages read the{' '}
              <Link
                href={UPBOUND_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="12px"
                color="purple.0"
                _hover={{ textDecoration: 'none' }}
              >
                Upbound Documentation
              </Link>{' '}
              or{' '}
              <Link
                href="mailto:info@upbound.io"
                rel="noopener noreferrer"
                fontSize="12px"
                color="purple.0"
                _hover={{ textDecoration: 'none' }}
              >
                Contact Us
              </Link>{' '}
              and we can help get you started.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageTemplate>
  );
};

export default PackageDetailsLayout;
